import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AppShell } from "../../components/Sidebar";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    full_name: user?.full_name || "",
    email: user?.email || "",
    mobile: user?.mobile || "",
    date_of_birth: user?.date_of_birth || "",
    gender: user?.gender || "",
    preferred_language: user?.preferred_language || "English",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/teacher/profile", {
        full_name: form.full_name,
        date_of_birth: form.date_of_birth,
        gender: form.gender,
        preferred_language: form.preferred_language,
      });
      await refreshUser();
      toast.success("Profile updated successfully");
      navigate("/teacher/settings");
    } catch {
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AppShell role="teacher">
      <Link to="/teacher/settings" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft size={14} /> Back to Settings
      </Link>
      <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">Edit Profile</h1>
      <p className="mt-1 text-slate-500">Update your personal information.</p>

      <form onSubmit={save} className="mt-6 max-w-2xl space-y-5 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <Field label="Full Name">
          <input data-testid="edit-fullname" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={input} />
        </Field>

        <Field label="Email Address" suffix={<Link to="/teacher/settings/change-email" className="text-sm font-semibold text-indigo-600 hover:underline">Change</Link>}>
          <input value={form.email} disabled className={`${input} bg-slate-50 text-slate-500`} />
        </Field>

        <Field label="Mobile Number" suffix={<Link to="/teacher/settings/change-mobile" className="text-sm font-semibold text-indigo-600 hover:underline">Change</Link>}>
          <input value={form.mobile} disabled className={`${input} bg-slate-50 text-slate-500`} />
        </Field>

        <Field label="Date of Birth">
          <input data-testid="edit-dob" placeholder="DD-MM-YYYY" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className={input} />
        </Field>

        <Field label="Gender">
          <select data-testid="edit-gender" value={form.gender} onChange={(e) => set("gender", e.target.value)} className={input}>
            <option value="">Select gender</option>
            <option>Male</option><option>Female</option><option>Other</option><option>Prefer not to say</option>
          </select>
        </Field>

        <Field label="Preferred Language">
          <select data-testid="edit-language" value={form.preferred_language} onChange={(e) => set("preferred_language", e.target.value)} className={input}>
            <option>English</option><option>Hindi</option><option>Marathi</option><option>Tamil</option><option>Bengali</option>
          </select>
        </Field>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={() => navigate("/teacher/settings")} className="rounded-xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
          <button data-testid="save-profile" disabled={saving} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-60">
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </AppShell>
  );
}

const input = "w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100";

const Field = ({ label, suffix, children }) => (
  <label className="block">
    <div className="mb-1.5 flex items-center justify-between">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {suffix}
    </div>
    {children}
  </label>
);
